import type { ViewPreset } from './presets';

// Default secondary text color (Brand theme D30). Used when a preset does not
// set its own `secondaryTextColor`. Kept next to the CSS-var mapping so the
// static stylesheet and the client runtime never drift apart.
export const DEFAULT_SECONDARY_TEXT_COLOR = '#595D70';

// Single source of truth for the CSS custom properties a preset contributes to
// `document.documentElement` (`:root`).
//
// Consumed by:
//   - the client runtime — `DisplayStore.initCssVariables()` (setProperty), and
//   - the build-time generator (scripts/generate-preset-css.cjs), which emits the
//     render-blocking static stylesheet (common/styles/presetVars.global.css,
//     gitignored) from the same mapping for every userType-keyed branded preset.
//
// The generated stylesheet is what themes the very first paint (keyed on
// <html data-preset="...">); this runtime call then re-asserts the identical
// values, so there is no visible "hop" from the default WDS tokens. Because both
// derive from this one function + presets.ts, the values are never duplicated.
// The CTA fill every branded preset already declares. It is the closest thing
// the preset system has to "this brand's accent colour".
const PRIMARY_BUTTON_FILL_VAR = '--wds-button-fill-standard-primary';

/**
 * Accent for the "Last used" badge, derived from the preset rather than picked
 * per brand.
 *
 * The badge sits on top of a social login button, so it has to belong to the
 * brand around it — a fixed colour reads as a sticker pasted onto Wixel purple
 * or Fade violet. Every branded preset already sets its CTA fill, so reusing it
 * themes the badge automatically and a new preset is covered the moment it
 * declares its own CTA.
 *
 * Returns nothing for the unbranded default, whose social buttons are
 * saturated (Google blue, Facebook navy) and would swallow an accent pill —
 * the stylesheet's ink fallback is the right answer there. A preset that wants
 * something other than its CTA colour can still set `--last-used-badge-bg`
 * directly in its own `cssVariables`.
 */
const getLastUsedBadgeBrandVar = (
  preset: ViewPreset,
): Record<string, string> => {
  const brandFill = preset.cssVariables?.[PRIMARY_BUTTON_FILL_VAR];
  return brandFill ? { '--last-used-badge-brand': brandFill } : {};
};

export const getPresetCssVars = (
  preset: ViewPreset,
): Record<string, string> => ({
  ...(preset.cssVariables ?? {}),
  ...getLastUsedBadgeBrandVar(preset),
  // Applied after the spread so an explicit override always wins, matching the
  // original initCssVariables() ordering.
  '--secondary-text-color-override':
    preset.secondaryTextColor || DEFAULT_SECONDARY_TEXT_COLOR,
});
